import { useMemo, useState } from "react";
import { HOLIDAY } from "@/lib/statutory";

export function HolidayCalculator() {
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [includeBankHolidays, setIncludeBankHolidays] = useState(true);
  const [proRata, setProRata] = useState(false);
  const [monthsWorked, setMonthsWorked] = useState(6);

  const result = useMemo(() => {
    const statutoryDays = Math.min(daysPerWeek * HOLIDAY.weeksPerYear, HOLIDAY.daysCap);
    // Pro-rate 8 UK bank holidays by working pattern (5-day full-time baseline).
    const bankHolidays = Math.round((8 * Math.min(daysPerWeek, 5)) / 5);
    const fullYearDays = includeBankHolidays ? statutoryDays : statutoryDays + bankHolidays;
    const proRatedDays = proRata
      ? (fullYearDays * Math.min(monthsWorked, 12)) / 12
      : fullYearDays;
    return {
      fullYearDays: round1(fullYearDays),
      proRatedDays: round1(proRatedDays),
      bankHolidays,
    };
  }, [daysPerWeek, includeBankHolidays, proRata, monthsWorked]);

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-foreground">Days worked per week</span>
          <input
            type="number"
            value={daysPerWeek}
            min={1}
            max={7}
            step={0.5}
            onChange={(e) => setDaysPerWeek(Number(e.target.value || 0))}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </label>
        <label className="flex items-end gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={includeBankHolidays}
            onChange={(e) => setIncludeBankHolidays(e.target.checked)}
            className="size-4 accent-[color:var(--accent)]"
          />
          Entitlement includes bank holidays
        </label>
        <label className="flex items-end gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={proRata}
            onChange={(e) => setProRata(e.target.checked)}
            className="size-4 accent-[color:var(--accent)]"
          />
          Pro-rata (started or leaving mid-year)
        </label>
        {proRata && (
          <label className="block">
            <span className="text-xs font-medium text-foreground">Months worked in the leave year</span>
            <input
              type="number"
              value={monthsWorked}
              min={0}
              max={12}
              step={0.5}
              onChange={(e) => setMonthsWorked(Number(e.target.value || 0))}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </label>
        )}
      </div>

      <div className="mt-8 rounded-2xl bg-brand p-6 text-brand-foreground">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">
          Statutory holiday entitlement
        </p>
        <p className="mt-3 font-serif text-5xl">{result.proRatedDays} days</p>
        <p className="mt-3 text-sm text-brand-foreground/70">
          Based on {HOLIDAY.weeksPerYear} weeks per year statutory minimum (Working Time Regulations 1998), capped at{" "}
          {HOLIDAY.daysCap} days for full-time employees.
          {includeBankHolidays ? " Bank holidays count towards this entitlement." : " Bank holidays are in addition."}
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            Every UK worker is entitled to a minimum of {HOLIDAY.weeksPerYear} weeks' paid holiday per year. The formula
            is: <span className="font-mono">days_per_week × {HOLIDAY.weeksPerYear}</span>, capped at {HOLIDAY.daysCap}{" "}
            days. Irregular-hours and part-year workers accrue at{" "}
            {(HOLIDAY.irregularAccrualRate * 100).toFixed(2)}% of hours worked.
          </p>
          <p>
            Employers can choose whether bank holidays are part of the statutory minimum or on top. Your contract should
            make this clear.
          </p>
        </div>
      </div>
    </div>
  );
}

function round1(n: number) {
  return Math.round(n * 10) / 10;
}
